import * as React from 'react';
import { ArrowLeftIcon, Cog8ToothIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import NextLink from 'next/link';
import { useTranslation } from 'next-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button, IconButton, Tooltip, Spinner } from '@restworld/ui-ds-base';
import { useWorkersService } from '@restworld/data-services';
import { formatDateTo, QUERY_KEYS, useAnalytics } from '@restworld/utils-common';

import { LayoutWrapper } from '../../layouts';
import ReferenceRequestSelectorModal, {
  referencesReqSelectorModalVisibleAtom,
} from './references-request-selector-modal';
import ReferencesRequestSuccessMessageModal from './references-request-message-modal';
import ReferencesSendLinkRequestModal from './references-send-link-request-modal';
import { isReferencesContentGeneralFlowAtom, useUserDeclineModalVisibility } from './profile-details-section';
import ReferencesConsentModal, { referencesConsentModalVisibleAtom } from './references-consent-modal';
import { useFetchWorkerDataQuery } from '../../hooks';
import { workerPersonalDetailsAtom } from '../global-state.worker-data';
import { NotFound } from '../legacy';
import { ReferenceCard } from '../legacy';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IWorkerReferencesProps {}

const useFetchListWorkerReferencesQuery = (params: { limit: number; offset: number }) => {
  const workerServices = useWorkersService();
  return useQuery({
    queryKey: [QUERY_KEYS.WORKERS.FETCH_WORKER_REFERENCES],
    queryFn: async () => {
      return (await workerServices.listWorkerReferences(params)).data.data;
    },
  });
};

const useUpdateWorkerReferenceMutation = () => {
  const workerServices = useWorkersService();
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();

  return useMutation({
    mutationFn: async (params: { visible_to_employers?: boolean; id: number }) => {
      return (await workerServices.updateWorkerReference(params)).data;
    },
    onSuccess: (data, params) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WORKERS.FETCH_WORKER_REFERENCES] });
      if (params.visible_to_employers) trackEvent('worker_visible_reference', {});
    },
  });
};

const WorkerReferencesHeader: React.FC<{ onRequestsClick: () => void }> = ({ onRequestsClick }) => {
  const { t } = useTranslation('worker-references-page');

  return (
    <>
      <div className="w-full flex justify-between items-center">
        <NextLink href="/profilo">
          <IconButton Icon={ArrowLeftIcon} ariaLabel="go-back" />
        </NextLink>
        {/* w-fit collapses the Tooltip's forced-w-full trigger span down to the icon, so the popover
            anchors to the icon (not the whole row); justify-between still pins it to the far right. */}
        <div className="w-fit">
          <Tooltip content={t('tooltip_message')} placement="top" className="w-10/12">
            <IconButton Icon={InformationCircleIcon} ariaLabel="information-tooltip" />
          </Tooltip>
        </div>
      </div>
      <div className="self-stretch inline-flex justify-between items-center">
        <h5 className="justify-start text-neutral-700 text-lg font-semibold">{t('title')}</h5>
        <Button
          onClick={onRequestsClick}
          StartIcon={Cog8ToothIcon}
          aria-label="your-references-request"
          className={'shadow-none'}
          variant="tertiary"
        >
          {t('your_requests')}
        </Button>
      </div>
    </>
  );
};

const WorkerReferencesList: React.FC<{ onManageRequests: () => void }> = ({ onManageRequests }) => {
  const { t } = useTranslation('worker-references-page');
  const { data: workerReferencesList, isLoading } = useFetchListWorkerReferencesQuery({ limit: 20, offset: 0 });
  const { mutate: updateWorkerReference, isPending } = useUpdateWorkerReferenceMutation();

  useFetchWorkerDataQuery();

  if (isLoading) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <Spinner size="lg" color="restworld" />
      </div>
    );
  }
  if (workerReferencesList?.length > 0) {
    return (
      <>
        {workerReferencesList.map((reference) => {
          const isNewReceivedReference = reference.visible_to_employers === null;
          return (
            <ReferenceCard
              key={reference.id}
              variant={isNewReceivedReference ? 'new_received' : reference.visible_to_employers ? 'visible' : 'hidden'}
              employerName={reference.referent_name}
              workingRelationship={reference.workplace_relationship}
              venueName={reference.worker_past_experience?.restaurant_name}
              referenceText={reference.message}
              actions={{
                onHideReference: () => updateWorkerReference({ id: reference.id, visible_to_employers: false }),
                onShowReference: () => {
                  updateWorkerReference({ id: reference.id, visible_to_employers: true });
                },
                loading: isPending,
              }}
              madeVisibleToEmployerAt={
                reference.made_visible_to_employer_at
                  ? formatDateTo(reference.made_visible_to_employer_at, 'dd/MM/yy')
                  : null
              }
            />
          );
        })}
      </>
    );
  }

  return (
    <NotFound
      mainTitle={t('no_references_received')}
      subTitle={t('request_reference_section')}
      ctaButtons={
        <Button variant="tertiary" fullwidth EndIcon={Cog8ToothIcon} className={'max-w-xs'} onClick={onManageRequests}>
          {t('manage_your_requests')}
        </Button>
      }
    />
  );
};

export const WorkerReferences: React.FunctionComponent<IWorkerReferencesProps> = () => {
  const { t } = useTranslation('worker-references-page');
  const consentToCollectReferences = useAtomValue(workerPersonalDetailsAtom)?.consentToCollectReferences;
  const setReferencesConsentModalVisible = useSetAtom(referencesConsentModalVisibleAtom);
  const setReferencesReqSelectorModalVisible = useSetAtom(referencesReqSelectorModalVisibleAtom);
  const setIsReferencesContentGeneralFlow = useSetAtom(isReferencesContentGeneralFlowAtom);

  const handleRequestsClick = React.useCallback(() => {
    if (!consentToCollectReferences) {
      setReferencesConsentModalVisible(true);
      setIsReferencesContentGeneralFlow(true);
    } else setReferencesReqSelectorModalVisible(true);
  }, [
    consentToCollectReferences,
    setReferencesReqSelectorModalVisible,
    setIsReferencesContentGeneralFlow,
    setReferencesConsentModalVisible,
  ]);

  useUserDeclineModalVisibility();
  return (
    <>
      <div className="relative w-full h-full flex min-h-screen flex-col justify-between items-center bg-neutral-10">
        <LayoutWrapper
          title={t('title')}
          hasFooter={false}
          hasNavbar={false}
          noPaddingBottom
          noHorizontalPadding
          noPaddingTop
        >
          <div className="px-4 py-5 max-w-[640px] mx-auto">
            <div className="inline-flex flex-col justify-start items-start gap-10 w-full relative">
              <WorkerReferencesHeader onRequestsClick={handleRequestsClick} />
              <WorkerReferencesList onManageRequests={handleRequestsClick} />
            </div>
          </div>
        </LayoutWrapper>
      </div>
      <ReferencesConsentModal />
      <ReferenceRequestSelectorModal />
      <ReferencesRequestSuccessMessageModal />
      <ReferencesSendLinkRequestModal />
    </>
  );
};

export default WorkerReferences;
